/**
 * SEO Landing Page Configurations
 * These pages target specific location + service keywords
 */

import type { LocationServiceConfig } from './LocationServicePage';

// Gardening services list
const gardeningServices = [
  'Lawn Mowing & Edging',
  'Hedge Trimming & Shaping',
  'Garden Bed Maintenance',
  'Weed Control',
  'Mulching & Soil Improvement',
  'Tree & Shrub Pruning',
  'Garden Clean-ups',
  'Green Waste Removal',
  'Seasonal Planting',
];

// Cleaning services list
const cleaningServices = [
  'Regular House Cleaning',
  'Deep Cleaning',
  'End of Lease / Bond Cleaning',
  'Window Cleaning',
  'Oven & Kitchen Cleaning',
  'Bathroom Sanitization',
  'Carpet & Floor Cleaning',
  'Hard Waste Removal',
  'One-Off Cleans',
];

// Taxidermy services list
const taxidermyServices = [
  'Bird Taxidermy',
  'Small Mammal Taxidermy',
  'Restoration & Repairs',
  'Custom Mounts',
  'Display Pieces',
  'Specimen Preservation',
];

// Common benefits
const gardeningBenefits = [
  'Experienced local gardener',
  'Reliable & punctual service',
  'Competitive rates',
  'No job too small',
  'Eco-friendly practices',
  'Free quotes available',
];

const cleaningBenefits = [
  'Thorough & detailed cleaning',
  'Reliable & trustworthy',
  'Bond-back guarantee available',
  'Flexible scheduling',
  'All cleaning supplies included',
  'Free quotes available',
];

const taxidermyBenefits = [
  'Expert craftsmanship',
  'Attention to natural detail',
  'Quality materials used',
  'Restoration specialists',
  'Tasmania-wide service',
  'Personalized consultation',
];

// All service areas
const allAreas = ['Hobart', 'New Norfolk', 'Glenorchy', 'Brighton', 'Bridgewater', 'Derwent Valley'];

export const seoPages: LocationServiceConfig[] = [
  // Gardening Pages
  {
    slug: 'gardening-hobart',
    location: 'Hobart',
    service: 'gardening',
    title: 'Gardening Services Hobart | Lawn Mowing & Garden Care | Cloud9 Tasmania',
    metaDescription: 'Professional gardening services in Hobart, Tasmania. Lawn mowing, hedge trimming, garden maintenance & more. Local, reliable service. Free quotes available.',
    heroTitle: 'Professional Gardening Services in Hobart',
    heroSubtitle: 'Transform your outdoor space with expert lawn care, hedge trimming, and garden maintenance services in Hobart and greater Hobart area.',
    intro: 'Looking for reliable gardening services in Hobart? Cloud9 Tasmania offers comprehensive lawn and garden care for residential properties throughout Hobart. From regular lawn mowing to complete garden makeovers, I provide quality workmanship at competitive prices.',
    services: gardeningServices,
    benefits: gardeningBenefits,
    nearbyAreas: allAreas.filter(a => a !== 'Hobart'),
  },
  {
    slug: 'gardening-new-norfolk',
    location: 'New Norfolk',
    service: 'gardening',
    title: 'Gardening Services New Norfolk | Lawn & Garden Care | Cloud9 Tasmania',
    metaDescription: 'Expert gardening services in New Norfolk, Derwent Valley. Lawn mowing, hedge trimming, garden maintenance. Local gardener, free quotes.',
    heroTitle: 'Expert Gardening Services in New Norfolk',
    heroSubtitle: 'Quality lawn care and garden maintenance for homes in New Norfolk and the Derwent Valley region.',
    intro: 'Need a reliable gardener in New Norfolk? As a local provider based in nearby Lachlan, Cloud9 Tasmania offers convenient and professional gardening services throughout New Norfolk and the Derwent Valley. I understand the unique requirements of gardens in our region.',
    services: gardeningServices,
    benefits: gardeningBenefits,
    nearbyAreas: allAreas.filter(a => a !== 'New Norfolk'),
  },
  {
    slug: 'gardening-glenorchy',
    location: 'Glenorchy',
    service: 'gardening',
    title: 'Gardening Services Glenorchy | Lawn Mowing & Maintenance | Cloud9 Tasmania',
    metaDescription: 'Professional gardening and lawn care services in Glenorchy, TAS. Regular maintenance, one-off jobs. Local, reliable, affordable. Get a free quote.',
    heroTitle: 'Professional Gardening Services in Glenorchy',
    heroSubtitle: 'Keep your Glenorchy property looking its best with professional lawn and garden care services.',
    intro: 'Cloud9 Tasmania provides expert gardening services to residents and businesses in Glenorchy. Whether you need regular lawn maintenance or a one-time garden tidy-up, I deliver reliable, quality service at competitive rates.',
    services: gardeningServices,
    benefits: gardeningBenefits,
    nearbyAreas: allAreas.filter(a => a !== 'Glenorchy'),
  },
  {
    slug: 'gardening-derwent-valley',
    location: 'Derwent Valley',
    service: 'gardening',
    title: 'Gardening Services Derwent Valley | Local Gardener | Cloud9 Tasmania',
    metaDescription: 'Local gardening services in Derwent Valley, Tasmania. Based in Lachlan. Lawn mowing, garden care, green waste removal. Free quotes.',
    heroTitle: 'Local Gardening Services in Derwent Valley',
    heroSubtitle: 'Your local gardener for the Derwent Valley region, based right here in Lachlan.',
    intro: 'As a Derwent Valley local based in Lachlan, Cloud9 Tasmania is your nearby choice for professional gardening services. I understand the local climate and soil conditions, ensuring your garden gets the care it needs to flourish.',
    services: gardeningServices,
    benefits: gardeningBenefits,
    nearbyAreas: allAreas.filter(a => a !== 'Derwent Valley'),
  },

  // Cleaning Pages
  {
    slug: 'cleaning-hobart',
    location: 'Hobart',
    service: 'cleaning',
    title: 'Cleaning Services Hobart | House Cleaning & Bond Clean | Cloud9 Tasmania',
    metaDescription: 'Professional house cleaning services in Hobart. Regular cleaning, deep cleaning, end of lease & bond cleaning. Reliable & thorough. Free quotes.',
    heroTitle: 'Professional Cleaning Services in Hobart',
    heroSubtitle: 'Reliable house cleaning, deep cleaning, and bond cleaning services for homes throughout Hobart.',
    intro: 'Looking for a reliable cleaner in Hobart? Cloud9 Tasmania offers thorough, professional cleaning services for homes throughout the Hobart area. From regular maintenance cleans to comprehensive end-of-lease cleaning, I deliver sparkling results every time.',
    services: cleaningServices,
    benefits: cleaningBenefits,
    nearbyAreas: allAreas.filter(a => a !== 'Hobart'),
  },
  {
    slug: 'cleaning-new-norfolk',
    location: 'New Norfolk',
    service: 'cleaning',
    title: 'Cleaning Services New Norfolk | House & Bond Cleaning | Cloud9 Tasmania',
    metaDescription: 'House cleaning services in New Norfolk, Derwent Valley. Regular cleans, bond cleaning, deep cleaning. Local, reliable cleaner. Get a quote.',
    heroTitle: 'Reliable Cleaning Services in New Norfolk',
    heroSubtitle: 'Quality house cleaning and bond cleaning for New Norfolk and Derwent Valley residents.',
    intro: 'Based nearby in Lachlan, Cloud9 Tasmania offers convenient and thorough cleaning services to New Norfolk residents. Whether you need regular house cleaning or a detailed end-of-lease clean, I provide reliable service with attention to detail.',
    services: cleaningServices,
    benefits: cleaningBenefits,
    nearbyAreas: allAreas.filter(a => a !== 'New Norfolk'),
  },
  {
    slug: 'cleaning-glenorchy',
    location: 'Glenorchy',
    service: 'cleaning',
    title: 'Cleaning Services Glenorchy | House Cleaning | Cloud9 Tasmania',
    metaDescription: 'Professional cleaning services in Glenorchy, TAS. House cleaning, bond cleaning, deep cleaning. Reliable & affordable. Free quotes available.',
    heroTitle: 'Quality Cleaning Services in Glenorchy',
    heroSubtitle: 'Thorough house cleaning and bond cleaning services for Glenorchy homes and rentals.',
    intro: 'Cloud9 Tasmania provides professional cleaning services to Glenorchy residents. My detailed approach ensures every corner of your home is spotless. I offer flexible scheduling to fit your busy lifestyle.',
    services: cleaningServices,
    benefits: cleaningBenefits,
    nearbyAreas: allAreas.filter(a => a !== 'Glenorchy'),
  },
  {
    slug: 'cleaning-derwent-valley',
    location: 'Derwent Valley',
    service: 'cleaning',
    title: 'Cleaning Services Derwent Valley | Local House Cleaner | Cloud9 Tasmania',
    metaDescription: 'Local cleaning services in Derwent Valley. Based in Lachlan. House cleaning, bond cleaning, hard waste removal. Free quotes.',
    heroTitle: 'Local Cleaning Services in Derwent Valley',
    heroSubtitle: 'Your local cleaner for the Derwent Valley, based right here in Lachlan.',
    intro: 'As your local Derwent Valley cleaning service based in Lachlan, Cloud9 Tasmania offers convenient, reliable house cleaning without the travel fees. I understand local needs and provide personalized service.',
    services: cleaningServices,
    benefits: cleaningBenefits,
    nearbyAreas: allAreas.filter(a => a !== 'Derwent Valley'),
  },

  // Taxidermy Pages
  {
    slug: 'taxidermy-tasmania',
    location: 'Tasmania',
    service: 'taxidermy',
    title: 'Taxidermy Services Tasmania | Bird & Mammal Taxidermist | Cloud9 Tasmania',
    metaDescription: 'Professional taxidermy services in Tasmania. Bird taxidermy, mammal preservation, restoration. Expert craftsmanship. Tasmania-wide service.',
    heroTitle: 'Professional Taxidermy Services in Tasmania',
    heroSubtitle: 'Expert bird and mammal taxidermy with meticulous attention to natural detail.',
    intro: 'Cloud9 Tasmania offers professional taxidermy services across the state. With a passion for preserving the natural beauty of specimens, I provide expert mounting, restoration, and custom display pieces. Each project receives careful attention to capture the essence of the animal.',
    services: taxidermyServices,
    benefits: taxidermyBenefits,
    nearbyAreas: ['Hobart', 'Launceston', 'Devonport', 'Burnie', 'All of Tasmania'],
  },
  {
    slug: 'bird-taxidermy-tasmania',
    location: 'Tasmania',
    service: 'taxidermy',
    title: 'Bird Taxidermy Tasmania | Avian Taxidermist | Cloud9 Tasmania',
    metaDescription: 'Expert bird taxidermy services in Tasmania. Native birds, game birds, waterfowl. Quality preservation & mounting. Statewide service.',
    heroTitle: 'Expert Bird Taxidermy in Tasmania',
    heroSubtitle: 'Specializing in the preservation and mounting of birds with natural, lifelike results.',
    intro: 'Cloud9 Tasmania specializes in bird taxidermy, capturing the beauty and character of avian specimens. From native Tasmanian birds to game birds and waterfowl, each mount is crafted with precision and care to create lasting, lifelike displays.',
    services: ['Native Bird Taxidermy', 'Game Bird Mounting', 'Waterfowl Preservation', 'Display Mounts', 'Restoration & Repairs', 'Custom Poses & Displays'],
    benefits: taxidermyBenefits,
    nearbyAreas: ['Hobart', 'Launceston', 'Devonport', 'Burnie', 'All of Tasmania'],
  },
];

export function getSeoPageBySlug(slug: string): LocationServiceConfig | undefined {
  return seoPages.find(page => page.slug === slug);
}
